/**
 * MKS-516 — answer controls of one pending Factory™ request on the task detail:
 * option buttons, a typed reply, and, for the staging review, the "Looks good" /
 * "Doesn't look good" verdict with an optional note about what should change.
 *
 * Mounted by `PendingQuestionsSection` ONLY when the server says the viewer may
 * answer (`canAnswer`), so the mutation hook and the toast context never run for
 * anybody else. Whether a click is allowed is the server's decision
 * (`answerAuthority`); this component only renders what the read told it to and
 * reports the outcome.
 */
import { useState } from 'react';
import { useAnswerPendingQuestion } from '../../hooks/usePendingTaskQuestions';
import { ApiClientError } from '../../services/api/client';
import type {
  AnswerOutcome,
  AnswerPendingQuestionInput,
  PendingTaskQuestion,
} from '../../services/api/paperclipQuestions';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { useToast, type ToastKind } from '../ui/Toast';

/** The ui_review option that means "Doesn't look good" (stored verdict `looks_wrong`). */
const DOESNT_LOOK_GOOD_INDEX = 1;

const CHANNEL_LABELS: Record<string, string> = {
  slack_button: 'Slack',
  slack_text: 'Slack',
  task_comment: 'a comment',
  monksheets_ui: 'Monksheets',
  web_button: 'Monksheets',
  web_text: 'Monksheets',
  admin: 'the admin page',
  github: 'GitHub',
};

/** The toast for one outcome of the answer route (pure, so the mapping is testable on its own). */
export function describeAnswerOutcome(outcome: AnswerOutcome): { message: string; kind: ToastKind } {
  switch (outcome.status) {
    case 'accepted':
      if (outcome.reworkScope && outcome.reworkScope.status !== 'accepted') {
        return {
          message: 'Your verdict was recorded, but your note about what should change was not.',
          kind: 'warning',
        };
      }
      return { message: 'Answer sent.', kind: 'success' };
    case 'already_answered': {
      const who = outcome.answeredByName ? ` by ${outcome.answeredByName}` : '';
      const via = outcome.answerChannel
        ? ` via ${CHANNEL_LABELS[outcome.answerChannel] ?? outcome.answerChannel}`
        : '';
      return { message: `Already answered${who}${via}.`, kind: 'info' };
    }
    case 'not_pending':
      return { message: 'This question is no longer open.', kind: 'info' };
    case 'not_authorized':
      return { message: "You can't answer this question", kind: 'error' };
    case 'invalid_choice':
      return { message: 'That answer is not valid for this question.', kind: 'error' };
  }
}

export interface PendingRequestAnswerControlsProps {
  taskId: number;
  item: PendingTaskQuestion;
}

export function PendingRequestAnswerControls({ taskId, item }: PendingRequestAnswerControlsProps) {
  const answer = useAnswerPendingQuestion(taskId);
  const toast = useToast();
  const [text, setText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const options = item.options ?? [];
  const isReview = item.kind === 'ui_review';

  async function send(input: AnswerPendingQuestionInput): Promise<boolean> {
    setBusy(true);
    try {
      const outcome = await answer.mutateAsync({ requestId: item.requestId, input });
      const { message, kind } = describeAnswerOutcome(outcome);
      toast(message, kind);
      return true;
    } catch (err) {
      if (err instanceof ApiClientError && err.statusCode === 403) {
        toast("You can't answer this question", 'error');
      } else if (err instanceof ApiClientError && err.statusCode === 404) {
        toast('This question no longer exists', 'error');
      } else {
        toast(err instanceof Error ? err.message : 'Could not send the answer', 'error');
      }
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function submitText() {
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    if (await send({ choice: { type: 'text', text: trimmed } })) setText('');
  }

  async function submitVerdict() {
    const trimmed = note.trim();
    const sent = await send({
      choice: { type: 'option', index: DOESNT_LOOK_GOOD_INDEX },
      ...(trimmed.length > 0 ? { reworkText: trimmed } : {}),
    });
    if (sent) {
      setModalOpen(false);
      setNote('');
    }
  }

  return (
    <div className="mt-1.5 space-y-1.5" data-testid={`pending-answer-controls-${item.requestId}`}>
      {options.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {options.map((option) => (
            <Button
              key={option.index}
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy}
              aria-label={`${option.label}: ${item.title}`}
              onClick={() => {
                if (isReview && option.index === DOESNT_LOOK_GOOD_INDEX) {
                  setModalOpen(true);
                  return;
                }
                void send({ choice: { type: 'option', index: option.index } });
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      )}

      {item.allowsText === true && (
        <div className="flex items-start gap-1.5">
          <textarea
            className="min-h-[2.25rem] flex-1 rounded-md border border-red-200 bg-white px-2 py-1 text-sm text-gray-900 focus:border-red-400 focus:outline-none"
            rows={2}
            value={text}
            disabled={busy}
            maxLength={4000}
            placeholder="Type your answer"
            aria-label={`Your answer: ${item.title}`}
            onChange={(e) => setText(e.target.value)}
          />
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={busy || text.trim().length === 0}
            onClick={() => void submitText()}
          >
            Send answer
          </Button>
        </div>
      )}

      {isReview && (
        <Modal
          isOpen={modalOpen}
          onClose={() => {
            if (!busy) setModalOpen(false);
          }}
          title="What doesn't look good?"
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
              <Button type="button" variant="primary" isLoading={busy} onClick={() => void submitVerdict()}>
                Send verdict
              </Button>
            </div>
          }
        >
          <label className="block text-sm font-medium text-gray-700" htmlFor={`rework-note-${item.requestId}`}>
            What should the Robot change? (optional)
          </label>
          <textarea
            id={`rework-note-${item.requestId}`}
            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none"
            rows={4}
            value={note}
            disabled={busy}
            maxLength={4000}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="mt-1 text-xs text-gray-500">
            Leave it empty and the Robot asks what should change in a separate prompt.
          </p>
        </Modal>
      )}
    </div>
  );
}
